import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarBlank, MapPin, CaretLeft, CaretRight, Trash, BookmarkSimple, List, CalendarDots, Clock, Storefront, CurrencyDollar, ArrowLeft
} from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyShows({ onViewEvent }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [savedEvents, setSavedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" or "calendar"
  const [calMonth, setCalMonth] = useState(new Date());
  const [removing, setRemoving] = useState(null);

  const fetchSaved = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/saved-shows/details`, { withCredentials: true });
      setSavedEvents(res.data.events || []);
    } catch (error) {
      console.error("Failed to load saved shows:", error.message);
      toast.error("Failed to load saved shows");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchSaved();
  }, [user, fetchSaved]);

  const handleRemove = async (eventId) => {
    setRemoving(eventId);
    try {
      await axios.delete(`${API}/auth/saved-shows/${eventId}`, { withCredentials: true });
      setSavedEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success("Show removed");
    } catch (error) {
      console.error("Failed to remove show:", error.message);
      toast.error("Failed to remove show");
    } finally {
      setRemoving(null);
    }
  };

  // Calendar logic
  const monthStart = startOfMonth(calMonth);
  const monthEnd = endOfMonth(calMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay(); // 0=Sun

  const eventsByDate = useMemo(() => {
    const map = {};
    savedEvents.forEach(ev => {
      try {
        const start = parseISO(ev.start_date);
        const end = parseISO(ev.end_date);
        let d = new Date(start);
        while (d <= end) {
          const key = format(d, "yyyy-MM-dd");
          if (!map[key]) map[key] = [];
          map[key].push(ev);
          d = new Date(d);
          d.setDate(d.getDate() + 1);
        }
      } catch (error) {
        console.error("Skipping malformed event dates:", error.message);
      }
    });
    return map;
  }, [savedEvents]);

  const getCategoryColor = (cat) => {
    const colors = { festival: "#E07A5F", fair: "#81B29A", community: "#3D405B" };
    return colors[cat] || "#6C757D";
  };

  const formatFee = (ev) => {
    if (ev.booth_fee) return ev.booth_fee;
    return "Contact for Info";
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-4" data-testid="my-shows-login-prompt">
        <BookmarkSimple weight="duotone" className="w-16 h-16 text-[#D6D3D1]" />
        <h2 className="text-xl font-semibold text-[#2B2D42]">Sign in to save shows</h2>
        <p className="text-[#6C757D] text-center max-w-md">
          Create an account to bookmark your favorite vendor opportunities and build your personal show calendar.
        </p>
        <Button onClick={() => navigate("/")} className="bg-[#E07A5F] hover:bg-[#D46A50] text-white">
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="my-shows-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-2 rounded-lg hover:bg-[#E8E4DF] transition-colors" data-testid="back-to-home">
            <ArrowLeft weight="bold" className="w-5 h-5 text-[#2B2D42]" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#2B2D42]" data-testid="my-shows-title">My Shows</h1>
            <p className="text-sm text-[#6C757D]">{savedEvents.length} saved show{savedEvents.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border border-[#D6D3D1] p-1" data-testid="view-toggle">
          <button
            onClick={() => setView("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${view === "list" ? "bg-[#E07A5F] text-white" : "text-[#6C757D] hover:text-[#2B2D42]"}`}
            data-testid="view-list-btn"
          >
            <List weight="bold" className="w-4 h-4" /> List
          </button>
          <button
            onClick={() => setView("calendar")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${view === "calendar" ? "bg-[#E07A5F] text-white" : "text-[#6C757D] hover:text-[#2B2D42]"}`}
            data-testid="view-calendar-btn"
          >
            <CalendarDots weight="bold" className="w-4 h-4" /> Calendar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-3 border-[#E07A5F] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : savedEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center" data-testid="no-saved-shows">
          <BookmarkSimple weight="duotone" className="w-16 h-16 text-[#D6D3D1]" />
          <h3 className="text-lg font-semibold text-[#2B2D42]">No saved shows yet</h3>
          <p className="text-[#6C757D] max-w-sm">Browse events and tap the bookmark icon to save shows you're interested in.</p>
          <Button onClick={() => navigate("/")} className="bg-[#81B29A] hover:bg-[#6A9A83] text-white">
            Browse Shows
          </Button>
        </div>
      ) : view === "list" ? (
        /* LIST VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="saved-shows-list">
          {savedEvents.map(ev => (
            <div
              key={ev.id}
              className="bg-white rounded-xl border border-[#D6D3D1] overflow-hidden hover:border-[#81B29A] transition-all group"
              data-testid={`saved-show-${ev.id}`}
            >
              <div className="flex">
                <div className="w-28 sm:w-36 flex-shrink-0 relative">
                  <img
                    src={ev.image_url}
                    alt={ev.name}
                    className="w-full h-full object-cover min-h-[120px]"
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400"; }}
                  />
                  <span
                    className="absolute bottom-1.5 left-1.5 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md text-white"
                    style={{ backgroundColor: getCategoryColor(ev.category) }}
                  >
                    {ev.category}
                  </span>
                </div>
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                  <div>
                    <h3 className="font-semibold text-[#2B2D42] text-sm sm:text-base truncate">{ev.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-[#6C757D] mt-1">
                      <MapPin weight="fill" className="w-3 h-3 text-[#E07A5F] flex-shrink-0" />
                      <span className="truncate">{ev.city}, {ev.state}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-[#6C757D] mt-0.5">
                      <CalendarBlank weight="fill" className="w-3 h-3 text-[#81B29A] flex-shrink-0" />
                      <span>{ev.start_date === ev.end_date ? format(parseISO(ev.start_date), "MMM d, yyyy") : `${format(parseISO(ev.start_date), "MMM d")} - ${format(parseISO(ev.end_date), "MMM d, yyyy")}`}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-[#6C757D] flex items-center gap-1">
                      <CurrencyDollar weight="bold" className="w-3 h-3" />
                      {formatFee(ev)}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {ev.vendor_app_url && (
                        <a
                          href={ev.vendor_app_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] font-medium text-[#81B29A] hover:text-[#6A9A83] px-2 py-1 rounded-md border border-[#81B29A]/30 hover:bg-[#81B29A]/5 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Apply
                        </a>
                      )}
                      <button
                        onClick={() => handleRemove(ev.id)}
                        disabled={removing === ev.id}
                        className="p-1.5 rounded-md text-[#E07A5F] hover:bg-[#E07A5F]/10 transition-colors"
                        data-testid={`remove-show-${ev.id}`}
                        title="Remove from saved"
                      >
                        <Trash weight="bold" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* CALENDAR VIEW */
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-4 sm:p-6" data-testid="calendar-view">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setCalMonth(prev => subMonths(prev, 1))} className="p-2 rounded-lg hover:bg-[#E8E4DF] transition-colors" data-testid="cal-prev-month">
              <CaretLeft weight="bold" className="w-5 h-5 text-[#2B2D42]" />
            </button>
            <h2 className="text-lg font-semibold text-[#2B2D42]" data-testid="cal-month-title">
              {format(calMonth, "MMMM yyyy")}
            </h2>
            <button onClick={() => setCalMonth(prev => addMonths(prev, 1))} className="p-2 rounded-lg hover:bg-[#E8E4DF] transition-colors" data-testid="cal-next-month">
              <CaretRight weight="bold" className="w-5 h-5 text-[#2B2D42]" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} className="text-center text-xs font-medium text-[#6C757D] py-1">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px bg-[#E8E4DF] border border-[#E8E4DF] rounded-lg overflow-hidden">
            {/* Empty cells for start offset */}
            {Array.from({ length: startDay }).map((_, i) => (
              <div key={`empty-${i}`} className="bg-[#F7F5F0] min-h-[80px] sm:min-h-[100px] p-1" />
            ))}
            {days.map(day => {
              const key = format(day, "yyyy-MM-dd");
              const dayEvents = eventsByDate[key] || [];
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={key}
                  className={`bg-white min-h-[80px] sm:min-h-[100px] p-1 ${isToday ? "ring-2 ring-inset ring-[#E07A5F]" : ""}`}
                  data-testid={`cal-day-${key}`}
                >
                  <span className={`text-xs font-medium block mb-0.5 ${isToday ? "text-[#E07A5F] font-bold" : "text-[#2B2D42]"}`}>
                    {format(day, "d")}
                  </span>
                  <div className="space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map(ev => (
                      <div
                        key={ev.id}
                        className="text-[9px] sm:text-[10px] leading-tight px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity text-white font-medium"
                        style={{ backgroundColor: getCategoryColor(ev.category) }}
                        title={ev.name}
                        onClick={() => onViewEvent && onViewEvent(ev)}
                        data-testid={`cal-event-${ev.id}`}
                      >
                        {ev.name}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <span className="text-[9px] text-[#6C757D] pl-1">+{dayEvents.length - 3} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 text-xs text-[#6C757D]">
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#E07A5F]" /> Festival</div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#81B29A]" /> Fair</div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#3D405B]" /> Community</div>
          </div>
        </div>
      )}
    </div>
  );
}
